import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import Brand from "./Brand.jsx";
import Shimmer, { LoadingStatus } from "./Shimmer.jsx";
import { formatUsdcCompact } from "../utils/format.js";
import { navItems, useInvestmentSummary, NavMaskIcon, SummaryRow } from "./navShared.jsx";

/**
 * Mobile slide-in drawer that mirrors the desktop Sidebar. Visible only on
 * small screens; the parent (Layout) renders the desktop Sidebar at md+
 * breakpoints and this drawer when the hamburger is tapped. Same nav items,
 * same yellow-on-dark-green active treatment, and the same Buy Token CTA +
 * Investment Summary card as the desktop sidebar so the two surfaces feel
 * identical.
 *
 * Closes on backdrop click, Escape, or after navigating to a new route.
 * Locks body scroll while open.
 */
export default function MobileDrawer({ open, onClose }) {
    // Wallet copy/disconnect now lives in the header dropdown
    // (WalletConnect → ConnectedDropdown); the drawer only consumes the
    // shared Investment Summary hook for its live figures.
    const location = useLocation();
    // Shared with the desktop Sidebar — single source of truth for the
    // chain-time interest accrual, Standard-only borrow cap, Turbo
    // filter, and shimmer gating. See navShared.jsx.
    const { tokens, activeCount, netYield, safeVault, showShimmer } =
        useInvestmentSummary();
    const summary = { tokens, activeCount, netYield, safeVault };

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
            <aside className="relative flex h-full w-72 max-w-[85%] flex-col overflow-y-auto bg-[#10281b] p-4 text-slate-200 shadow-2xl">
                <div className="flex items-center justify-between">
                    <Link to="/" onClick={onClose} className="px-1" aria-label="GILDer home">
                        <Brand tone="light" />
                    </Link>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"
                    >
                        <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <line x1="5" y1="5" x2="15" y2="15" />
                            <line x1="15" y1="5" x2="5" y2="15" />
                        </svg>
                    </button>
                </div>

                <nav className="mt-6 flex-1 space-y-2">
                    {navItems.map((item) => {
                        const forceActive =
                            item.matchPaths?.includes(location.pathname) ||
                            item.matchPrefixes?.some((p) =>
                                p.endsWith("/")
                                    ? location.pathname.startsWith(p)
                                    : location.pathname === p
                            );
                        return (
                            <NavLink
                                key={item.label}
                                to={item.to}
                                end={item.to === "/dashboard"}
                                onClick={onClose}
                                className={({ isActive }) =>
                                    `relative flex items-center gap-3 rounded-md py-2 pl-5 pr-3 text-[13px] font-normal transition-colors ${
                                        isActive || forceActive
                                            ? "bg-[#2d3d22] text-[#FBC206]"
                                            : "text-[#e6c985] hover:text-[#FBC206]"
                                    }`
                                }
                            >
                                {({ isActive }) => {
                                    const active = isActive || forceActive;
                                    return (
                                        <>
                                            {active ? (
                                                <span className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-[#FBC206]" />
                                            ) : null}
                                            <NavMaskIcon src={item.icon} size={14} />
                                            <span>{item.label}</span>
                                        </>
                                    );
                                }}
                            </NavLink>
                        );
                    })}
                </nav>

                <hr className="mt-3 mb-6 border-t-2 border-white/15" />

                <Link
                    to="/marketplace"
                    onClick={onClose}
                    aria-label="Buy $GILD token"
                    className="mx-auto inline-flex w-[80%] items-center justify-center gap-2 rounded-md bg-[#FBC206] px-3 py-1.5 text-[13px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
                >
                    Buy Token
                </Link>

                <div className="mx-auto mt-3 w-[90%] rounded-lg bg-[#0E2C19] ring-1 ring-white/5 px-3 py-2">
                    <div className="whitespace-nowrap text-[9px] font-light uppercase tracking-wider text-white text-center">
                        Investment Summary
                    </div>
                    <ul className="mt-1.5 space-y-1 text-[10px]" aria-busy={showShimmer || undefined}>
                        {showShimmer && <LoadingStatus as="li" label="Loading investment summary…" />}
                        <SummaryRow
                            label="Tokens"
                            value={
                                showShimmer ? (
                                    <Shimmer width="1.5rem" height="0.625rem" tone="light" />
                                ) : (
                                    String(summary.tokens)
                                )
                            }
                            valueColor="text-[#FBC206]"
                        />
                        <SummaryRow
                            label="Active Bonds"
                            value={
                                showShimmer ? (
                                    <Shimmer width="1.5rem" height="0.625rem" tone="light" />
                                ) : (
                                    String(summary.activeCount)
                                )
                            }
                            valueColor="text-[#FBC206]"
                        />
                        <SummaryRow
                            label="Net Yield"
                            value={
                                showShimmer ? (
                                    <Shimmer width="2.5rem" height="0.625rem" tone="light" />
                                ) : (
                                    <span className="inline-flex items-center gap-1">
                                        {summary.netYield}
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                                    </span>
                                )
                            }
                            valueColor="text-[#FBC206]"
                        />
                        <SummaryRow
                            label="Available to borrow"
                            value={
                                showShimmer ? (
                                    <Shimmer width="3rem" height="0.625rem" tone="light" />
                                ) : (
                                    `$${formatUsdcCompact(summary.safeVault)}`
                                )
                            }
                            valueColor="text-[#FBC206]"
                        />
                    </ul>
                </div>

                {/* Address copy + Disconnect moved to the header
                    dropdown (WalletConnect → ConnectedDropdown) so they
                    live in one place across desktop + mobile chrome. */}
            </aside>
        </div>,
        document.body
    );
}

